import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Download,
  FileText,
  FileImage,
  Calendar,
  CheckCircle,
  X,
  Settings,
  BarChart3,
  TrendingUp,
  Users,
  AlertTriangle,
  Target,
  Star
} from 'lucide-react';
import { reportService } from '../lib/reports';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ReportSection {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

interface CustomReportBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  currentPeriod: string;
}

export const CustomReportBuilder: React.FC<CustomReportBuilderProps> = ({
  isOpen,
  onClose,
  currentPeriod
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'json' | 'pdf'>('csv');
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportSections, setReportSections] = useState<ReportSection[]>([
    {
      id: 'overview',
      name: 'Executive Summary',
      description: 'Key metrics and performance overview',
      icon: <BarChart3 className="w-5 h-5" />,
      enabled: true
    },
    {
      id: 'performance',
      name: 'Performance Analysis',
      description: 'Detailed performance metrics and trends',
      icon: <TrendingUp className="w-5 h-5" />,
      enabled: true
    },
    {
      id: 'platforms',
      name: 'Platform Performance',
      description: 'Platform-specific compliance and performance data',
      icon: <Target className="w-5 h-5" />,
      enabled: true
    },
    {
      id: 'creatives',
      name: 'Top Performing Creatives',
      description: 'Best performing creative assets and rankings',
      icon: <Star className="w-5 h-5" />,
      enabled: true
    },
    {
      id: 'issues',
      name: 'Compliance Issues',
      description: 'Common compliance problems and trends',
      icon: <AlertTriangle className="w-5 h-5" />,
      enabled: true
    },
    {
      id: 'trends',
      name: 'Monthly Trends',
      description: 'Historical performance and trend analysis',
      icon: <Calendar className="w-5 h-5" />,
      enabled: true
    },
    {
      id: 'team',
      name: 'Team Performance',
      description: 'Team member productivity and performance',
      icon: <Users className="w-5 h-5" />,
      enabled: false
    }
  ]);

  const toggleSection = (sectionId: string) => {
    setReportSections(sections =>
      sections.map(section =>
        section.id === sectionId
          ? { ...section, enabled: !section.enabled }
          : section
      )
    );
  };

  const generatePDFReport = async (reportData: any) => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    
    // Title
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Rulevia Analytics Report', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 15;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${selectedPeriod} | Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 20;

    const enabledSections = reportSections.filter(s => s.enabled);
    
    for (const section of enabledSections) {
      // Check if we need a new page
      if (yPosition > pageHeight - 40) {
        pdf.addPage();
        yPosition = 20;
      }
      
      // Section header
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(section.name, 20, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      switch (section.id) {
        case 'overview':
          pdf.text(`Total Analyses: ${reportData.overview.totalAnalyses}`, 20, yPosition);
          yPosition += 6;
          pdf.text(`Launch Ready Rate: ${reportData.overview.launchReadyRate}%`, 20, yPosition);
          yPosition += 6;
          pdf.text(`Average Score: ${reportData.overview.avgScore}`, 20, yPosition);
          yPosition += 6;
          pdf.text(`Credits Used: ${reportData.overview.creditsUsed}`, 20, yPosition);
          yPosition += 10;
          break;
          
        case 'performance':
          pdf.text(`Average Performance Score: ${reportData.performance.avgPerformanceScore}%`, 20, yPosition);
          yPosition += 6;
          pdf.text(`Top Performing Platform: ${reportData.performance.topPerformingPlatform}`, 20, yPosition);
          yPosition += 6;
          pdf.text(`Best Creative Type: ${reportData.performance.bestCreativeType}`, 20, yPosition);
          yPosition += 10;
          break;
          
        case 'platforms':
          reportData.platformStats.slice(0, 5).forEach((platform: any) => {
            pdf.text(`${platform.name}: ${platform.analyses} analyses, ${platform.launchReadyRate}% ready`, 20, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
          break;
          
        case 'creatives':
          reportData.topPerformingCreatives.slice(0, 5).forEach((creative: any) => {
            pdf.text(`${creative.name}: ${creative.performanceScore}% performance`, 20, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
          break;
          
        case 'issues':
          reportData.topIssues.slice(0, 5).forEach((issue: any) => {
            pdf.text(`${issue.issue}: ${issue.count} occurrences`, 20, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
          break;
          
        case 'trends':
          reportData.monthlyTrends.slice(0, 6).forEach((trend: any) => {
            pdf.text(`${trend.month}: ${trend.analyses} analyses (${trend.approved} approved)`, 20, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
          break;
      }
      
      yPosition += 5;
    }
    
    return pdf;
  };

  const handleGenerateReport = async () => {
    try {
      setIsGenerating(true);
      const enabledSectionIds = reportSections.filter(s => s.enabled).map(s => s.id);
      
      if (enabledSectionIds.length === 0) {
        alert('Please select at least one report section');
        return;
      }

      if (selectedFormat === 'pdf') {
        // For PDF, we need to get the full report data first
        const reportData = await reportService.getReportData(selectedPeriod);
        const pdf = await generatePDFReport(reportData);
        
        const filename = `rulevia-custom-report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.pdf`;
        pdf.save(filename);
      } else {
        // For CSV and JSON
        const reportContent = await reportService.exportCustomReport(
          selectedPeriod,
          enabledSectionIds,
          selectedFormat
        );
        
        const mimeType = selectedFormat === 'csv' ? 'text/csv' : 'application/json';
        const fileExtension = selectedFormat === 'csv' ? 'csv' : 'json';
        
        const blob = new Blob([reportContent], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rulevia-custom-report-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      
      onClose();
    } catch (error) {
      console.error('Error generating custom report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Custom Report Builder</h2>
                  <p className="text-gray-600 text-sm">Create a customized analytics report</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Time Period Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Period
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Time</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
            </div>

            {/* Format Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Export Format
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setSelectedFormat('csv')}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    selectedFormat === 'csv'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <FileText className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm font-medium">CSV</span>
                </button>
                <button
                  onClick={() => setSelectedFormat('json')}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    selectedFormat === 'json'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <FileText className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm font-medium">JSON</span>
                </button>
                <button
                  onClick={() => setSelectedFormat('pdf')}
                  className={`p-3 border rounded-lg text-center transition-colors ${
                    selectedFormat === 'pdf'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <FileImage className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-sm font-medium">PDF</span>
                </button>
              </div>
            </div>

            {/* Section Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Report Sections
              </label>
              <div className="space-y-2">
                {reportSections.map((section) => (
                  <div
                    key={section.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      section.enabled
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          section.enabled
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          {section.icon}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{section.name}</h4>
                          <p className="text-sm text-gray-600">{section.description}</p>
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        section.enabled
                          ? 'border-purple-500 bg-purple-500'
                          : 'border-gray-300'
                      }`}>
                        {section.enabled && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {reportSections.filter(s => s.enabled).length} sections selected
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateReport}
                  disabled={isGenerating || reportSections.filter(s => s.enabled).length === 0}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center space-x-2 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Generate Report</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}; 